import type { ProjectDetail, WikiVersionSnapshot } from "@esi/shared";
import { ArrowLeft, History, RotateCcw } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { canChange } from "../projects/capabilities.ts";
import { Link, pageHistoryPath, wikiPath } from "../router.tsx";
import { DiffView } from "./DiffView.tsx";
import { useWikiPage, useWikiVersionCompare } from "./queries.ts";
import { RestoreDialog, type RestoreTarget } from "./RestoreDialog.tsx";

/**
 * Two versions of a page side by side as one line diff, oldest first. Only the two versions are
 * shown; the versions between them contribute nothing but what the diff contains.
 */
export function PageCompare(props: {
  project: ProjectDetail;
  pageId: string;
  from: number | null;
  to: number | null;
}) {
  const { project, pageId, from, to } = props;
  if (from === null || to === null) {
    return <CompareNotFound project={project} pageId={pageId} />;
  }
  return <Comparison project={project} pageId={pageId} from={from} to={to} />;
}

function CompareNotFound({ project, pageId }: { project: ProjectDetail; pageId: string }) {
  const { t } = useTranslation();
  return (
    <section data-esi-content="wiki" className="mx-auto flex max-w-3xl flex-col gap-4">
      <Notice>{t("wiki.compare.notFound")}</Notice>
      <Link
        href={wikiPath(project.id, pageId)}
        className="text-sm text-accent-text hover:underline"
      >
        {t("wiki.compare.backToPage")}
      </Link>
    </section>
  );
}

function Comparison(props: { project: ProjectDetail; pageId: string; from: number; to: number }) {
  const { project, pageId } = props;
  const { t } = useTranslation();
  const compared = useWikiVersionCompare(project.id, pageId, props.from, props.to);
  // The current version decides whether the older side may be restored.
  const current = useWikiPage(project.id, pageId, undefined, compared.isSuccess);
  const [restore, setRestore] = useState<RestoreTarget | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  if (compared.isPending) {
    return <Skeleton className="h-48" />;
  }
  if (compared.isError) {
    // A refusal reads as not found; any other failure (an outage) says what it is.
    return compared.error instanceof ApiError && compared.error.status === 404 ? (
      <CompareNotFound project={project} pageId={pageId} />
    ) : (
      <Notice tone="danger">{compared.error.message}</Notice>
    );
  }
  const { from, to } = compared.data;
  const canRestore =
    canChange(project, "wiki.draft") && !!current.data && from.version !== current.data.version;
  const side = (snapshot: WikiVersionSnapshot) => (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Link
        href={wikiPath(project.id, pageId, snapshot.version)}
        className="text-sm font-medium text-fg hover:underline"
      >
        {t("wiki.version", { version: snapshot.version })}
      </Link>
      <p className="text-xs text-fg-secondary">
        {snapshot.authorName ?? t("wiki.draft.provenance.someone")} ·{" "}
        <span className="tabular-nums">{formatDateTime(snapshot.publishedAt)}</span>
      </p>
    </div>
  );
  return (
    <section className="mx-auto flex max-w-3xl flex-col gap-5">
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <Link
            href={wikiPath(project.id, pageId)}
            className="inline-flex min-w-0 items-center gap-1.5 text-accent-text hover:underline"
          >
            <ArrowLeft aria-hidden="true" className="size-4 shrink-0" />
            <span className="truncate">{to.title}</span>
          </Link>
          <Link
            href={pageHistoryPath(project.id, pageId)}
            className="inline-flex items-center gap-1.5 text-accent-text hover:underline"
          >
            <History aria-hidden="true" className="size-4" />
            {t("wiki.docs.history")}
          </Link>
        </div>
        <h2 className="text-xl font-semibold tracking-tight text-fg">{t("wiki.compare.title")}</h2>
        <div className="grid grid-cols-1 gap-3 border-y border-line py-3 sm:grid-cols-2">
          {side(from)}
          {side(to)}
        </div>
        {from.title !== to.title ? (
          <p className="break-words text-sm text-fg-secondary">
            {t("wiki.compare.titleChange", { from: from.title, to: to.title })}
          </p>
        ) : null}
        {from.pageType !== to.pageType ? (
          <p className="text-sm text-fg-secondary">
            {t("wiki.compare.typeChange", {
              from: t(`wiki.pageTypes.${from.pageType}`),
              to: t(`wiki.pageTypes.${to.pageType}`),
            })}
          </p>
        ) : null}
        {canRestore ? (
          <div>
            <Button
              ref={trigger}
              variant="outline"
              size="sm"
              onClick={() =>
                setRestore({
                  kind: "version",
                  pageId,
                  version: from.version,
                  title: current.data?.title ?? from.title,
                })
              }
            >
              <RotateCcw aria-hidden="true" />
              {t("wiki.history.restoreVersion", { version: from.version })}
            </Button>
          </div>
        ) : null}
      </header>
      <DiffView
        before={from.markdown}
        after={to.markdown}
        version={from.version}
        noChanges={t("wiki.compare.noChanges")}
        tooLarge={{
          notice: t("wiki.compare.tooLarge"),
          before: t("wiki.version", { version: from.version }),
          after: t("wiki.version", { version: to.version }),
        }}
      />
      <RestoreDialog
        projectId={project.id}
        target={restore}
        onClose={() => setRestore(null)}
        returnFocus={() => trigger.current}
      />
    </section>
  );
}
